import React, { FC, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { hideLoading, showLoading } from 'reducks/loading/operations'
import { acceptInvitation, getIsInvitationExpired } from 'reducks/staff/operations'
import { push } from 'connected-react-router'
import { Spacer } from 'components/Utility'
import { RedLogo } from 'components/Image'
import { Description } from 'components/Typography'
import { TextInput } from 'components/Form'
import { OrangeButton } from 'components/Button'
import { useStringChangeEvent, useToggle } from 'hooks/form'
import { getConfirmPasswordError, getEmailError, getPasswordError } from 'reducks/error/selectors'
import { isValidConfirmPassword, isValidEmailFormat, isValidPassword } from 'reducks/error/operations'

const RegisterTemplate: FC = () => {
  const dispatch = useDispatch()
  const queryToken = useSelector((state: State) => state.router.location.query.token)
  // 開いたときの招待トークンを使う。登録後に完了画面へ遷移すると query から token がなくなるため、
  // query の変化に合わせて読み直すと、完了画面へ遷移する途中でエラー画面に遷移してしまう
  const [token] = useState<string>(queryToken ?? '')
  const confirmPasswordError = useSelector((state: State) => getConfirmPasswordError(state))
  const emailError = useSelector((state: State) => getEmailError(state))
  const passwordError = useSelector((state: State) => getPasswordError(state))

  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [isPasswordHidden, togglePasswordHidden] = useToggle(true)
  const [isConfirmPasswordHidden, toggleConfirmPasswordHidden] = useToggle(true)

  const handleEmail = useStringChangeEvent(setEmail)
  const handlePassword = useStringChangeEvent(setPassword)
  const handleConfirmPassword = useStringChangeEvent(setConfirmPassword)

  // 招待の有効期限をチェックする
  // 結果が届く前に画面を離れた（登録して完了画面へ遷移した等）ときは、結果を使わない
  useEffect(() => {
    let isCancelled = false
    ;(async () => {
      // 招待トークンのないリンク（旧形式のリンクを含む）は使えない
      if (!token) {
        dispatch(push('/register/error'))
        return
      }
      await dispatch(showLoading())
      const isExpired = await getIsInvitationExpired(token)
      dispatch(hideLoading())
      if (isExpired && !isCancelled) {
        dispatch(push('/register/error'))
      }
    })()
    return () => {
      isCancelled = true
    }
  }, [token])

  return (
    <div className={styles.standard}>
      <Spacer size={40} />
      <RedLogo />
      <Spacer size={24} />
      <Description align={'center'} description={'管理画面にログインするための設定を行います。'} />
      <Spacer size={48} />
      <TextInput
        date-testid={'email'}
        disabled={false}
        error={emailError}
        isFilled={email !== ''}
        label={'メールアドレス'}
        onChange={handleEmail}
        onBlur={() => dispatch(isValidEmailFormat(email))}
        required={true}
        placeholder={'example@sacri.jp'}
        type={'email'}
        value={email}
      />
      <Spacer size={28} />
      <TextInput
        date-testid={'password'}
        disabled={false}
        error={passwordError}
        icon={isPasswordHidden ? '/icon/show_password.svg' : '/icon/hide_password.svg'}
        isFilled={password !== ''}
        label={'パスワード'}
        onBlur={() => dispatch(isValidPassword(password))}
        onChange={handlePassword}
        onClick={togglePasswordHidden}
        required={true}
        placeholder={'6桁以上の英数字'}
        type={isPasswordHidden ? 'password' : 'text'}
        value={password}
      />
      <Spacer size={28} />
      <TextInput
        date-testid={'password'}
        disabled={false}
        error={confirmPasswordError}
        icon={isConfirmPasswordHidden ? '/icon/show_password.svg' : '/icon/hide_password.svg'}
        isFilled={confirmPassword !== ''}
        label={'パスワード（確認）'}
        onBlur={() => dispatch(isValidConfirmPassword(password, confirmPassword))}
        onChange={handleConfirmPassword}
        onClick={toggleConfirmPasswordHidden}
        required={true}
        placeholder={'6桁以上の英数字'}
        type={isConfirmPasswordHidden ? 'password' : 'text'}
        value={confirmPassword}
      />
      <Spacer size={28} />
      <OrangeButton
        disabled={
          email === '' ||
          password === '' ||
          confirmPassword === '' ||
          password !== confirmPassword ||
          emailError !== '' ||
          passwordError !== '' ||
          confirmPasswordError !== ''
        }
        label={'登録する'}
        onClick={() => dispatch(acceptInvitation(email, password, token))}
        size={'medium'}
        type={'main'}
      />
    </div>
  )
}

export default RegisterTemplate
